import React, { useState, useRef, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../Auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Heart, 
  MapPin, 
  Clock, 
  Car, 
  Users, 
  Eye,
  Expand,
  Share2,
  Facebook,
  Twitter,
  Mail,
  Copy,
  MessageCircle,
  ChevronLeft,
  ChevronRight,
  Check,
  Bookmark,
  Sun,
  Cloud,
  Footprints
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { UserMention } from '../ui/UserLink';
import ClickableTag from '../ui/ClickableTag';
import { ClickableMetadataBadge } from '../ui/ClickableMetadataBadge';
import useUserInteractions from '../../hooks/useUserInteractions';
import AddVisitModal from '../Modals/AddVisitModal';
import AddToWishlistButton from '../ui/AddToWishlistButton';
import {
  WEATHER_OPTIONS, LIGHT_OPTIONS, CROWD_LEVEL_OPTIONS,
  DIFFICULTY_OPTIONS, TIME_WINDOW_OPTIONS, getIcon, getLabel
} from '../../constants/spotEnums';

function SpotCard({ spot, index, isSelected, displayMode = 'list' }) {
  const { state, actions } = useApp();
  const { isAuthenticated } = useAuth();
  const { isSpotLiked, isSpotVisited, getVisitCount, toggleLike, addVisit } = useUserInteractions();
  const navigate = useNavigate();
  const [shareDropdownOpen, setShareDropdownOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showAddVisitModal, setShowAddVisitModal] = useState(false);
  const [tagCategories, setTagCategories] = useState([]);
  const shareDropdownRef = useRef(null);

  // Load tag categories for clickable tags
  useEffect(() => {
    const fetchTagCategories = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tag-categories`);
        if (response.ok) {
          const categories = await response.json();
          setTagCategories(categories);
        }
      } catch (error) {
        console.error('Error fetching tag categories:', error);
      }
    };
    fetchTagCategories();
  }, []);

  // Reset current image index when spot changes
  React.useEffect(() => {
    setCurrentImageIndex(0);
  }, [spot.id]);

  // Close share dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (shareDropdownRef.current && !shareDropdownRef.current.contains(event.target)) {
        setShareDropdownOpen(false);
      }
    };

    if (shareDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [shareDropdownOpen]);

  const navigateImage = (direction, e) => {
    e.stopPropagation(); // Prevent card click
    const total = spot.photos?.length || 0;
    if (total <= 1) return;
    
    if (direction === 'next') {
      setCurrentImageIndex((prev) => (prev + 1) % total);
    } else {
      setCurrentImageIndex((prev) => (prev - 1 + total) % total);
    }
  };

  const calculateDistance = () => {
    // Use pre-calculated distance from spot processing if available
    if (spot.distanceFromUser !== undefined) {
      return spot.distanceFromUser;
    }

    // Fallback to manual calculation (for backward compatibility)
    if (state.userLocation && spot.latitude && spot.longitude) {
      const R = 6371; // Earth's radius in km
      const dLat = (spot.latitude - state.userLocation[0]) * Math.PI / 180;
      const dLon = (spot.longitude - state.userLocation[1]) * Math.PI / 180;
      const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(state.userLocation[0] * Math.PI / 180) * Math.cos(spot.latitude * Math.PI / 180) * 
        Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      const distance = R * c;
      return Math.round(distance * 10) / 10; // Round to 1 decimal
    }
    return null; // Return null instead of random distance when no user location
  };

  const getFormattedDistance = () => {
    // Use pre-formatted distance if available
    if (spot.distanceFormatted !== undefined) {
      return spot.distanceFormatted;
    }

    // Fallback to manual formatting
    const distance = calculateDistance();
    if (distance === null) return null;
    
    if (distance < 1) {
      return `${Math.round(distance * 1000)} m`;
    } else {
      return `${distance.toFixed(1)} km`;
    }
  };

  const handleCardClick = () => {
    actions.setSelectedSpot(spot);
    actions.setCurrentSpotIndex(index);
    actions.setCurrentPhotoIndex(0);
    
    // Center map on spot
    if (state.mapRef) {
      state.mapRef.setView([spot.latitude, spot.longitude], 12);
    }
  };

  const handleShowDetails = (e) => {
    e.stopPropagation();
    actions.setSelectedSpot(spot);
    actions.setShowSpotDetails(true);
  };

  const handleImageClick = (e, photoIndex) => {
    e.stopPropagation();
    
    // Use TikTok Gallery instead of lightbox for consistency
    actions.openSpotGallery(spot, photoIndex);
  };

  const handleLike = async (e) => {
    e.stopPropagation();
    
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    // Optimistic UI update - change immediately
    const currentlyLiked = isSpotLiked(spot.id);
    const oldLikeCount = spot.likes_count || spot.likes || 0;
    const newLikeCount = currentlyLiked 
      ? Math.max(oldLikeCount - 1, 0)  // Unlike: -1
      : oldLikeCount + 1;               // Like: +1
    
    // Update UI immediately
    actions.updateSpot({ 
      ...spot, 
      likes: newLikeCount,
      likes_count: newLikeCount 
    });
    
    try {
      await toggleLike(spot.id);
    } catch (error) {
      // Revert on error
      actions.updateSpot({ 
        ...spot, 
        likes: oldLikeCount,
        likes_count: oldLikeCount 
      });
      console.error('Error liking spot:', error);
      alert('Erreur lors du like: ' + error.message);
    }
  };

  const handleVisitClick = (e) => {
    e.stopPropagation();
    
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    setShowAddVisitModal(true);
  };

  const handleAddVisit = async (visitData) => {
    try {
      await addVisit(spot.id, visitData);
    } catch (error) {
      console.error('Error adding visit:', error);
      throw error;
    }
  };

  const handleShare = (e, platform) => {
    e.stopPropagation();
    const spotUrl = `${window.location.origin}/spot/${spot.id}?details=true`;
    const shareText = `Découvrez ce magnifique spot : ${spot.title} - ${spot.description.substring(0, 100)}...`;
    
    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(spotUrl)}&quote=${encodeURIComponent(shareText)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(spotUrl)}&text=${encodeURIComponent(shareText)}&hashtags=SunSeat,Nature,Detente`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + spotUrl)}`, '_blank');
        break;
      case 'email':
        window.open(`mailto:?subject=${encodeURIComponent('Découvrez ce spot sur Sun-Spot')}&body=${encodeURIComponent(shareText + '\n\nVoir le spot : ' + spotUrl)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(spotUrl).then(() => {
          alert('Lien copié dans le presse-papier !');
        }).catch(err => {
          console.error('Erreur lors de la copie:', err);
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = spotUrl;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          alert('Lien copié dans le presse-papier !');
        });
        break;
      default:
        break;
    }
    setShareDropdownOpen(false);
  };

  const isLiked = isAuthenticated ? isSpotLiked(spot.id) : false;
  const isVisited = isAuthenticated ? isSpotVisited(spot.id) : false;
  const visitCount = isAuthenticated ? getVisitCount(spot.id) : 0;
  
  const distance = calculateDistance();
  const formattedDistance = getFormattedDistance();

  // Conditional styling based on display mode
  const cardClasses = displayMode === 'grid' 
    ? `transition-all duration-200 hover:shadow-lg cursor-pointer bg-white rounded-lg ${
        isSelected ? 'ring-2 ring-blue-500 shadow-lg' : ''
      }`
    : `transition-all duration-200 hover:shadow-lg cursor-pointer ${
        isSelected ? 'ring-2 ring-blue-500 shadow-lg' : ''
      }`;

  const cardStyle = displayMode === 'grid' 
    ? { width: '100%', minHeight: '300px' } // Compact for grid - like in the image
    : { minWidth: '320px', minHeight: '400px' }; // Original size for list

  return (
    <Card 
      className={cardClasses}
      onClick={handleCardClick}
      style={cardStyle}
    >
      <CardHeader className="p-0">
        {/* Image Carousel */}
        <div className={`relative overflow-hidden rounded-t-lg ${
          displayMode === 'grid' ? 'h-40' : 'h-48'
        }`}>
          {spot.photos && spot.photos.length > 0 ? (
            <div className="relative group">
              <img
                src={spot.photos[currentImageIndex].url || spot.photos[currentImageIndex]}
                alt={spot.photos[currentImageIndex].metadata?.title || spot.title}
                className={`w-full object-cover transition-transform duration-300 group-hover:scale-105 cursor-pointer ${
                  displayMode === 'grid' ? 'h-40' : 'h-48'
                }`}
                onClick={(e) => handleImageClick(e, currentImageIndex)}
              />
              
              {/* Expand overlay on hover */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center pointer-events-none">
                <Expand className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" size={displayMode === 'grid' ? 24 : 32} />
              </div>
              
              {/* Navigation arrows - only if multiple photos */}
              {spot.photos.length > 1 && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => navigateImage('prev', e)}
                    className={`absolute left-1 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white hover:bg-opacity-70 opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${
                      displayMode === 'grid' ? 'w-6 h-6 p-0' : 'w-8 h-8 p-0'
                    }`}
                  >
                    <ChevronLeft size={displayMode === 'grid' ? 14 : 18} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => navigateImage('next', e)}
                    className={`absolute right-1 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white hover:bg-opacity-70 opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${
                      displayMode === 'grid' ? 'w-6 h-6 p-0' : 'w-8 h-8 p-0'
                    }`}
                  >
                    <ChevronRight size={displayMode === 'grid' ? 14 : 18} />
                  </Button>
                </>
              )}
              
              {/* Photo count indicator - only if multiple photos */}
              {spot.photos.length > 1 && (
                <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded-full text-xs">
                  {currentImageIndex + 1}/{spot.photos.length}
                </div>
              )}
              
              {/* Likes and Comments indicators - only in grid mode */}
              {displayMode === 'grid' && (
                <div className="absolute top-2 left-2 flex space-x-2">
                  {/* Like indicator */}
                  <div className="bg-black bg-opacity-50 text-white px-2 py-1 rounded-full text-xs flex items-center space-x-1">
                    <Heart size={10} className={isLiked ? 'fill-current text-red-400' : 'text-white'} />
                    <span>{spot.likes || 0}</span>
                  </div>
                  
                  {/* Comments button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Ouvrir la modal de détails et scroll vers les commentaires
                      actions.setSelectedSpot(spot);
                      actions.setShowSpotDetails(true);
                      
                      // Scroll vers les commentaires après un délai
                      setTimeout(() => {
                        const commentsSection = document.querySelector('[data-section="comments"]');
                        if (commentsSection) {
                          commentsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }, 300);
                    }}
                    className="bg-black bg-opacity-50 text-white px-2 py-1 rounded-full text-xs flex items-center space-x-1 hover:bg-opacity-70"
                  >
                    <MessageCircle size={10} />
                    <span>{spot.comments_count || spot.comments?.length || 0}</span>
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className={`w-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center ${
              displayMode === 'grid' ? 'h-40' : 'h-48'
            }`}>
              <MapPin className="text-blue-400" size={displayMode === 'grid' ? 32 : 48} />
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className={displayMode === 'grid' ? 'p-3' : 'p-4'}>
        {/* Title only for grid mode, title + distance for list mode */}
        {displayMode === 'grid' ? (
          <div className="mb-2">
            <h3 className="font-semibold text-base text-gray-800 leading-tight mb-1">
              {spot.title}
            </h3>
            <p className="text-gray-600 text-xs">
              Par <UserMention username={spot.author || 'anonyme'} size="xs" />
            </p>
          </div>
        ) : (
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-semibold text-lg text-gray-800 leading-tight">
              {spot.title}
            </h3>
            <div className="flex items-center text-sm text-gray-500 ml-2">
              <MapPin size={14} className="mr-1" />
              <span>{formattedDistance ? `${formattedDistance}` : 'Position inconnue'}</span>
            </div>
          </div>
        )}

        {/* Description - shorter in grid mode */}
        <p className={`text-gray-600 mb-3 line-clamp-2 ${
          displayMode === 'grid' ? 'text-xs' : 'text-sm'
        }`}>
          {displayMode === 'grid' 
            ? (spot.description.length > 60 ? spot.description.substring(0, 60) + '...' : spot.description)
            : spot.description
          }
        </p>


        {/* Practical Info - Only in list mode */}
        {displayMode === 'list' && (
          <div className="space-y-2 mb-3">
            {/* Nouvelles métadonnées v2 - Badges cliquables */}
            {spot.v2_data && (
              <div className="flex flex-wrap gap-1.5">
                {/* Créneaux horaires recommandés */}
                {spot.v2_data.recommended_time_windows?.slice(0, 2).map((tw) => (
                  <ClickableMetadataBadge 
                    key={tw.type}
                    category="time_windows"
                    value={tw.type}
                    size="xs"
                  />
                ))}
                
                {/* Conditions météo idéales */}
                {spot.v2_data.ideal_conditions?.weather?.slice(0, 2).map((w) => (
                  <ClickableMetadataBadge 
                    key={w}
                    category="weather"
                    value={w}
                    size="xs"
                  />
                ))}
                
                {/* Niveau d'affluence */}
                {spot.v2_data.crowd_level && (
                  <ClickableMetadataBadge 
                    category="crowd_level"
                    value={spot.v2_data.crowd_level}
                    size="xs"
                  />
                )}
                
                {/* Difficulté d'accès */}
                {spot.v2_data.accessibility?.difficulty && (
                  <ClickableMetadataBadge 
                    category="difficulty"
                    value={spot.v2_data.accessibility.difficulty}
                    size="xs"
                  />
                )}
              </div>
            )}
            
            {/* Fallback vers l'ancien format practical_info */}
            {!spot.v2_data && spot.practical_info && (
              <div className="grid grid-cols-3 gap-2 text-xs text-gray-600">
                {spot.practical_info.recommended_hours && (
                  <div className="flex items-center">
                    <Clock size={12} className="mr-1 text-blue-500" />
                    <span className="truncate">{spot.practical_info.recommended_hours}</span>
                  </div>
                )}
                {spot.practical_info.accessibility && (
                  <div className="flex items-center">
                    <Car size={12} className="mr-1 text-green-500" />
                    <span className="truncate">{spot.practical_info.accessibility}</span>
                  </div>
                )}
                {spot.practical_info.occupation_level && (
                  <div className="flex items-center">
                    <Users size={12} className="mr-1 text-orange-500" />
                    <span className="truncate">{spot.practical_info.occupation_level}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Actions - simplified for grid mode */}
        {/* Tags - only show once, appropriate for mode */}
        {spot.tags && spot.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {spot.tags.slice(0, displayMode === 'grid' ? 3 : 5).map((tagSlug) => {
              const tag = state.allTags.find(t => t.slug === tagSlug);
              const category = state.tagCategories.find(c => c.id === tag?.category_id);
              
              return (
                <ClickableTag
                  key={tagSlug}
                  tag={tag || { slug: tagSlug, name: tagSlug }}
                  category={category}
                  size="xs"
                />
              );
            })}
            {spot.tags.length > (displayMode === 'grid' ? 3 : 5) && (
              <span className="text-xs text-gray-500 py-1">
                +{spot.tags.length - (displayMode === 'grid' ? 3 : 5)} autres
              </span>
            )}
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          {displayMode === 'grid' ? (
            // Grid mode - simplified actions like in your image
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  // Localiser sur la carte
                  if (state.mapRef) {
                    state.mapRef.setView([spot.latitude, spot.longitude], 12);
                  }
                }}
                className="text-xs text-gray-600 hover:text-blue-600 p-1"
              >
                📍 Localiser sur la carte
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleShowDetails}
                className="text-xs px-3 py-1 bg-green-600 hover:bg-green-700 text-white border-green-600"
              >
                Plus de détails
              </Button>
            </>
          ) : (
            // List mode - full actions
            <>
              {/* Like Button */}
              <Button
                variant={isLiked ? "default" : "ghost"}
                size="sm"
                onClick={handleLike}
                className={`flex items-center space-x-1 ${
                  isLiked ? 'bg-red-500 hover:bg-red-600 text-white' : 'hover:bg-red-50 hover:text-red-600'
                }`}
              >
                <Heart size={16} className={isLiked ? 'fill-current' : ''} />
                <span>{spot.likes || 0}</span>
              </Button>

              {/* Visited Button */}
              <Button
                variant={isVisited ? "default" : "ghost"}
                size="sm"
                onClick={handleVisitClick}
                className={`flex items-center space-x-1 ${
                  isVisited 
                    ? 'bg-green-500 hover:bg-green-600 text-white' 
                    : 'hover:bg-green-50 hover:text-green-600'
                }`}
                title={isVisited ? `Visité ${visitCount} fois` : 'Marquer comme visité'}
              >
                <Check size={16} className={isVisited ? 'fill-current' : ''} />
                <span>
                  {isVisited ? `Visité${visitCount > 1 ? ` (${visitCount})` : ''}` : 'Visité'}
                </span>
              </Button>

              {/* Comments Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  // Ouvrir la modal de détails et scroll vers les commentaires
                  actions.setSelectedSpot(spot);
                  actions.setShowSpotDetails(true);
                  
                  // Scroll vers les commentaires après un délai pour laisser la modal s'ouvrir
                  setTimeout(() => {
                    const commentsSection = document.querySelector('[data-section="comments"]');
                    if (commentsSection) {
                      commentsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 300);
                }}
                className="flex items-center space-x-1 text-sm text-gray-500 hover:text-blue-600 hover:bg-blue-50"
              >
                <MessageCircle size={14} />
                <span>{spot.comments_count || spot.comments?.length || 0}</span>
              </Button>

              {/* Action Buttons */}
              <div className="flex items-center space-x-1">
                {/* Share Button with Dropdown */}
                <div className="relative" ref={shareDropdownRef}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShareDropdownOpen(!shareDropdownOpen);
                    }}
                    className="hover:bg-blue-50 hover:text-blue-600"
                  >
                    <Share2 size={16} />
                  </Button>
                  
                  {shareDropdownOpen && (
                    <div className="absolute bottom-full mb-2 right-0 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-50">
                      <div className="flex space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleShare(e, 'facebook')}
                          className="p-2 hover:bg-blue-50"
                        >
                          <Facebook size={16} className="text-blue-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleShare(e, 'twitter')}
                          className="p-2 hover:bg-blue-50"
                        >
                          <Twitter size={16} className="text-blue-400" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleShare(e, 'whatsapp')}
                          className="p-2 hover:bg-green-50"
                        >
                          <MessageCircle size={16} className="text-green-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleShare(e, 'email')}
                          className="p-2 hover:bg-gray-50"
                        >
                          <Mail size={16} className="text-gray-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleShare(e, 'copy')}
                          className="p-2 hover:bg-gray-50"
                        >
                          <Copy size={16} className="text-gray-600" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Wishlist Button */}
                <div onClick={(e) => {
                  if (!isAuthenticated) {
                    e.stopPropagation();
                    navigate('/login');
                  }
                }}>
                  <AddToWishlistButton 
                    spot={spot}
                    size="sm"
                    variant="ghost"
                  />
                </div>

                {/* Details Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleShowDetails}
                  className="hover:bg-blue-50 hover:border-blue-300"
                >
                  <Eye size={16} className="mr-1" />
                  <span>Détails</span>
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Author - only in list mode */}
        {displayMode === 'list' && (
          <div className="mt-2 text-xs text-gray-500">
            Par <UserMention username={spot.author || 'anonyme'} size="xs" />
          </div>
        )}
      </CardContent>

      {/* Add Visit Modal */}
      <AddVisitModal
        open={showAddVisitModal}
        onClose={() => setShowAddVisitModal(false)}
        spot={spot}
        onAddVisit={handleAddVisit}
      />
    </Card>
  );
}

export default SpotCard;